<template>
  <ion-page>
    <ion-content :fullscreen="true" class="bg-[#fec629]">
      <template v-if="isAuthenticated">
        <p class="hidden">{{ getToken() }}</p>
      </template>
      <!-- The local persona picker deliberately does NOT live here. In local
           mode the router guard redirects every tokenless path to /login, so
           this page is unreachable before auth and the picker would never
           render. It lives in pages/login/index.vue instead (ONE-7804). -->
      <div
        v-if="!isAuthenticated"
        class="relative w-screen h-screen bg-[#fec629] flex flex-col overflow-hidden"
      >
        <!-- HOGC Logo - controls its own positioning for no-shift transition -->
        <HOGCLogo />

        <!-- Buttons Section -->
        <div class="buttons-section">
          <div
            class="flex flex-row gap-2 w-[80%] login-button bg-[#322A21] rounded-[100px] mx-auto items-center text-center justify-center mt-button"
            @click="pushToLogin"
          >
            <div class="login-button-text text-white font-aktiv-grotesk-medium">
              Sign in with HOGC One
            </div>
            <div>
              <img class="login-icon" src="/assets/icons/login-icon.svg" />
            </div>
          </div>
          <div
            class="flex flex-row gap-2 w-[80%] login-button rounded-[100px] mx-auto items-center text-center justify-center border border-[#1D395B] border-solid mt-button"
            @click="openPage"
          >
            <div class="login-button-text text-[#21201E] font-aktiv-grotesk-medium">
              I'm new here
            </div>
            <div>
              <img class="login-icon" src="/assets/icons/register-icon.svg" />
            </div>
          </div>
          <div
            class="w-[80%] login-button mx-auto flex items-center text-center justify-center mt-button"
          >
            <div class="login-disclaimer text-[#21201E] font-aktiv-grotesk-medium">
              By logging in or registering, you agree to our
              <span class="text-[#1D395B] underline"
                ><a href="https://hogc.one/terms-of-use" target="_blank">Terms of Use</a></span
              >
              and
              <span class="text-[#1D395B] underline"
                ><a href="https://hogc.one/privacy" target="_blank">Privacy Policy</a></span
              >
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage, isPlatform } from '@ionic/vue';
import { Browser } from '@capacitor/browser';
import { openAuthUrl } from '@/utils/safe-browser-open';
import { useAuth0, getAccessTokenSilentlyWithMonitoring } from '@/utils/auth0';
import { mapActions } from 'vuex';
import { createLogoutUri } from '../utils/redirect-uri';
import { useRoute } from 'vue-router';
import { computed, ref, watch } from 'vue';
import { SplashScreen } from '@capacitor/splash-screen';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import HOGCLogo from '@/components/HOGCLogo.vue';
import { buildPendingDeepLinkAppState } from '@/utils/deeplink';
import {
  trackAuthStarted,
  trackNavigationClick,
  trackRegistrationStarted,
} from '@/utils/analytics';

export default {
  name: 'PreSignInPage',
  components: {
    IonPage,
    IonContent,
    HOGCLogo,
  },
  setup() {
    const route = useRoute();
    const { loginWithRedirect, isAuthenticated, checkSession } = useAuth0();
    const isBrowserOpen = ref(false);
    const isNative = isPlatform('android') || isPlatform('ios');
    const isNativeBrowser = isPlatform('mobileweb');

    const login = async () => {
      const appState = buildPendingDeepLinkAppState();

      try {
        await loginWithRedirect({
          ...(appState ? { appState } : {}),
          openUrl: url => openAuthUrl(url, { source: 'pre-sign-in' }),
        });
        isBrowserOpen.value = true;
      } catch (error) {
        // Never leave this as an unhandled rejection: main.js's
        // unhandledrejection handler answers an auth failure with another
        // logout and another browser open, which fails the same way and feeds
        // the retry watcher below (#1965).
        console.error('[PreSignIn] Login redirect failed:', error?.message);
        isBrowserOpen.value = false;
      }
    };

    function delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    if (isNative && !isNativeBrowser) {
      watch(isBrowserOpen, async value => {
        // Only trigger login if browser closes and we're still on pre-sign-in page
        // This prevents accidental login triggers when navigating away
        if (!value) {
          if (route.path !== '/pre-sign-in') {
            return;
          }
          await delay(1000);
          if (!isAuthenticated.value) {
            await login();
          }
        }
      });
    }

    return {
      isAuthenticated,
      login,
      isBrowserOpen,
      delay,
      isNative,
      isNativeBrowser,
      checkSession,
    };
  },
  data() {
    return {
      token: '',
    };
  },
  created() {
    // Ensure we're on the correct route - don't trigger any login-related logic
    const currentPath = this.$route?.path || '';
    if (currentPath !== '/pre-sign-in') {
      console.warn('[PreSignIn] Component created but not on /pre-sign-in route:', currentPath);
    }
    setTimeout(async () => {
      await SplashScreen.hide();
    }, 1000);
  },
  async mounted() {
    // Ensure we're on the correct route - prevent any accidental navigation to login
    const currentPath = this.$route?.path || '';
    if (currentPath !== '/pre-sign-in') {
      console.warn('[PreSignIn] Component mounted but not on /pre-sign-in route:', currentPath);
      return;
    }

    // Delay redirect to show splash screen animation
    await new Promise(resolve => setTimeout(resolve, 5000));
  },
  methods: {
    isIos() {
      return isIosNativeOnly();
    },
    isAndroid() {
      return isAndroidNativeOnly();
    },
    ...mapActions('user', ['setToken']),
    async getToken() {
      const currentPath = this.$route?.path || '';

      if (process.env.VUE_APP_NODE_ENV === 'local') {
        this.token = localStorage.getItem('token');
      } else {
        const { logout, isAuthenticated } = useAuth0();
        try {
          // Fix A — default cacheMode 'on' reads cache then silently refreshes via refresh-token grant. Only 'on' | 'off' | 'cache-only' are valid per @auth0/auth0-spa-js.
          this.token = await getAccessTokenSilentlyWithMonitoring(
            { cacheMode: 'on' },
            'pre-sign-in',
          );
        } catch (err) {
          // Fix A — terminal auth errors only require a logout flow when the
          // user actually had a session. On pre-sign-in, the common case is
          // "no session" (fresh install or just logged out) — there's nothing
          // to log out of, so we let the user stay on this page and tap Sign
          // In, which will run loginWithRedirect(). Calling auth0.logout()
          // here would redirect through the deep-link handler and bounce the
          // user back into the pre-sign-in catch path → infinite loop.
          const terminalErrors = ['login_required', 'invalid_grant', 'missing_refresh_token'];
          if (terminalErrors.includes(err?.error) && isAuthenticated?.value === true) {
            console.warn(
              '[Fix-A Auth0] pre-sign-in terminal error for authenticated user, logging out:',
              err?.error,
            );
            // Fix C — wipe analytics identity before handing off to Auth0 so
            // the next user on this device starts from a clean slate.
            const { clearAnalyticsSessions } = await import('@/utils/clear-analytics-sessions');
            await clearAnalyticsSessions({ preserveAnalyticsIdentity: true });
            logout({
              logoutParams: {
                returnTo: createLogoutUri(
                  process.env.VUE_APP_PACKAGE_ID,
                  process.env.VUE_APP_AUTH0_DOMAIN,
                ),
              },
              openUrl: async url =>
                await Browser.open({
                  url,
                  windowName: '_self',
                }),
            });
          } else if (terminalErrors.includes(err?.error)) {
            console.log(
              '[Fix-A Auth0] pre-sign-in: no prior session, staying on this screen for interactive login:',
              err?.error,
            );
          }
        }
      }

      if (currentPath.includes('login')) {
        this.$router.push('/pre-loading');
      }
      if (currentPath.includes('pre-sign-in')) {
        if (!localStorage.getItem('deepLinkPathObj')) {
          this.$router.push('/home');
        }
      }

      this.setToken(this.token);
    },
    pushToLogin() {
      trackAuthStarted({
        provider: 'auth0',
        source: 'preSignIn',
      });

      trackNavigationClick({
        label: 'sign-in-hogc-one',
        page: 'pre-sign-in',
        destination: '/login',
        source: 'preSignIn',
      });

      // Explicitly navigate to login only when user clicks the button
      // This ensures we don't accidentally trigger login component lifecycle hooks
      this.$router.push({ path: '/login', query: { fromPreSignIn: 'true' } });
    },
    pushToRegister() {
      trackRegistrationStarted({
        flow: 'public',
        source: 'preSignIn',
      });

      trackNavigationClick({
        label: 'register',
        page: 'pre-sign-in',
        destination: '/registration',
        source: 'preSignIn',
      });

      this.$router.push('/registration');
    },
    openPage() {
      trackRegistrationStarted({
        flow: 'public',
        source: 'preSignIn',
      });

      trackNavigationClick({
        label: 'new-here-registration',
        page: 'pre-sign-in',
        destination: '/registration',
        source: 'preSignIn',
      });

      this.$router.push('/registration');
    },
  },
};
</script>

<style scoped>
ion-content {
  --ion-background-color: #fec629;
  --overflow: hidden;
}

ion-content::-webkit-scrollbar {
  display: none;
}

::v-deep(.swiper-pagination-bullet) {
  background-color: grey-20;
}

::v-deep(.swiper-pagination-bullet-active) {
  background-color: #00a9e6;
}

::v-deep(.swiper-wrapper) {
  padding-left: 0;
}

::v-deep(.swiper-slide) {
  margin-right: 0;
}

::v-deep(.swiper-container) {
  padding-bottom: 0;
}

/* Buttons positioned below logo with adaptive gap */
.buttons-section {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fec629;
  padding: clamp(10px, 1.5vh, 16px);
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  height: 30vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-button {
  height: 5.5vh;
}

.login-button-text {
  font-size: 2vh;
}

.login-icon {
  width: 2vh;
  height: 2vh;
}

.login-disclaimer {
  font-size: 1.5vh;
  line-height: 1.7vh;
}

.mt-button {
  margin-top: 1vh;
}

/* iPad/tablet (768px - 1023px) */
@media (min-width: 768px) {
  .buttons-section > div {
    width: 40% !important;
  }

  .buttons-section {
    height: 30vh !important;
  }

  .login-button {
    height: 4.5vh !important;
  }

  .login-button-text {
    font-size: 1.8vh !important;
  }

  .login-icon {
    width: clamp(12px, 1.8vh, 18px) !important;
    height: clamp(12px, 1.8vh, 18px) !important;
  }

  .login-disclaimer {
    font-size: 1.3vh !important;
    line-height: 1.5vh !important;
  }

  .mt-button {
    margin-top: 0.7vh !important;
  }
}

/* Desktop (≥ 1024px) */
@media (min-width: 1024px) {
  .buttons-section > div {
    width: 40% !important;
  }

  .login-button {
    height: 7vh !important;
  }

  .login-button-text {
    font-size: 2vh !important;
  }

  .login-icon {
    width: 2vh !important;
    height: 2vh !important;
  }

  .login-disclaimer {
    font-size: 1.5vh !important;
    line-height: 1.7vh !important;
  }

  .mt-button {
    margin-top: 0.9vh !important;
  }
}
</style>
